import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Dimensions,
  Pressable,
  Animated,
} from 'react-native';
import { GlassBackdrop } from './glass';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Lock, Delete, Check } from 'lucide-react-native';
import useKeydown from '../hooks/useKeydown';
import { authApi } from '../api/auth';
import { ApiError } from '../api/client';
import { useAuthStore } from '../state/authStore';
import { colors, fontFamily } from '../theme';

// Opaque stand-in for the glass where there's no backdrop blur (Android, web).
const SOLID_FALLBACK = '#FFFBF5';

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  // Optional "Forgot PIN?" recovery. When provided, a link is shown that hands
  // off to the password-based PIN reset (the parent re-authenticates with
  // their account password). Omitted on surfaces with no recovery route (TV).
  onForgot?: () => void;
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'ok'];

// Phone-vs-TV/desktop branching — same idiom as ChildHomeScreen.
const IS_PHONE =
  Platform.OS !== 'web' && Dimensions.get('window').width < 768;

// ─────────────────────────────────────────────────────────────────────────────
// Parent PIN gate, styled to match the liquid-glass tab bar: a floating glass
// slab over a dimmed page, circular keys, and a lock badge that says what this
// is before the title does.
//
// Notes on the redesign:
//   • Keys are round, not square — they read as buttons at a glance and give
//     comfortably >44pt touch targets.
//   • The confirm key is the only filled element, so the primary action is
//     obvious; delete stays quiet.
//   • A wrong PIN shakes the dot row instead of only swapping a line of text —
//     the feedback lands where the eye already is.
//   • The TV focus ring is preserved (this same component runs on the TV, driven
//     by useKeydown), just restyled as a bright halo.
// ─────────────────────────────────────────────────────────────────────────────
export default function PinGate({ onSuccess, onCancel, onForgot }: Props) {
  const [pin, setPin] = useState('');
  const [focused, setFocused] = useState(4);
  const [error, setError] = useState(false);
  // When the backend tells us no PIN is set, we surface a friendlier message
  // and an "Exit anyway" escape hatch instead of the generic "Wrong PIN".
  const [noPinSet, setNoPinSet] = useState(false);
  const { accessToken } = useAuthStore();
  const insets = useSafeAreaInsets();

  // Shake the dots on a rejected PIN.
  const shake = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!error) return;
    Animated.sequence([
      Animated.timing(shake, { toValue: 1, duration: 55, useNativeDriver: true }),
      Animated.timing(shake, { toValue: -1, duration: 55, useNativeDriver: true }),
      Animated.timing(shake, { toValue: 0.6, duration: 55, useNativeDriver: true }),
      Animated.timing(shake, { toValue: 0, duration: 55, useNativeDriver: true }),
    ]).start();
  }, [error, shake]);

  const handleKeyPress = (key: string) => {
    setError(false);
    if (key === 'clear') {
      setPin('');
      return;
    }
    if (key === 'ok') {
      verifyPin();
      return;
    }
    if (pin.length < 4) setPin((p) => p + key);
  };

  const verifyPin = async () => {
    if (pin.length < 4) return;
    try {
      await authApi.verifyPin(pin, accessToken!);
      onSuccess();
    } catch (e) {
      // The backend distinguishes "wrong PIN" (401) from "no PIN configured"
      // (400 BadRequestException — see auth.service.ts:verifyPin). The old
      // catch lumped them together, so a parent who never set a PIN saw
      // "Wrong PIN. Try again." forever with no way out. Branch on status.
      if (e instanceof ApiError && e.status === 400) {
        setNoPinSet(true);
        setPin('');
      } else {
        setError(true);
        setPin('');
      }
    }
  };

  useKeydown({
    isActive: true,
    left: () => setFocused((f) => (f % 3 === 0 ? f : f - 1)),
    right: () => setFocused((f) => (f % 3 === 2 ? f : f + 1)),
    up: () => setFocused((f) => (f < 3 ? f : f - 3)),
    down: () => setFocused((f) => (f >= 9 ? f : f + 3)),
    ok: () => handleKeyPress(KEYS[focused]),
    back: onCancel,
  });

  const translateX = shake.interpolate({
    inputRange: [-1, 1],
    outputRange: [-9, 9],
  });

  return (
    // Backdrop is a Pressable so tapping outside the card dismisses on phone.
    // TV remote already has `back: onCancel` via useKeydown above.
    <Pressable style={styles.overlay} onPress={onCancel}>
      {/* Stop propagation by intercepting taps on the card itself. */}
      <Pressable
        style={[
          styles.card,
          // Phone: a bottom sheet flush with the screen edge, so the keypad sits
          // under the thumb. Padding clears the home indicator. TV/desktop keep
          // the centred card — a sheet glued to the bottom of a television makes
          // no sense with a remote.
          IS_PHONE && styles.cardSheet,
          IS_PHONE && { paddingBottom: Math.max(insets.bottom, 12) + 12 },
        ]}
        onPress={() => {}}
      >

        <GlassBackdrop blurAmount={26} fallbackColor={SOLID_FALLBACK} />
        <View
          style={[styles.rim, IS_PHONE && styles.rimSheet]}
          pointerEvents="none"
        />

        {/* Sheet grab handle. Must come after the BlurView/rim — those are
            absolute-fill and would paint over it. */}
        {IS_PHONE && <View style={styles.grabber} pointerEvents="none" />}

        <TouchableOpacity
          style={styles.closeBtn}
          onPress={onCancel}
          accessibilityLabel="Close"
          accessibilityRole="button"
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        >
          <Text style={styles.closeBtnText}>×</Text>
        </TouchableOpacity>

        {/* Lock badge — says "this is locked" before the words do. */}
        <View style={styles.badge}>
          <Lock size={22} color={colors.primaryInk} strokeWidth={2.4} />
        </View>

        <Text style={styles.title}>Parent mode</Text>
        <Text style={styles.subtitle}>
          Enter your 4-digit PIN to unlock the parent area.
        </Text>

        <Animated.View style={[styles.dots, { transform: [{ translateX }] }]}>
          {[0, 1, 2, 3].map((i) => (
            <View
              key={i}
              style={[
                styles.dot,
                pin.length > i && styles.dotFilled,
                error && styles.dotError,
              ]}
            />
          ))}
        </Animated.View>

        {error ? (
          <Text style={styles.errorText}>That PIN didn&apos;t match.</Text>
        ) : (
          <View style={styles.errorSpacer} />
        )}

        {noPinSet && (
          <View style={styles.noPinBlock}>
            <Text style={styles.noPinText}>
              No Parent PIN has been set yet. You can set one in Me → PIN.
            </Text>
            <TouchableOpacity
              style={styles.exitAnywayBtn}
              onPress={onSuccess}
              accessibilityLabel="Exit anyway"
              accessibilityRole="button"
            >
              <Text style={styles.exitAnywayText}>Continue anyway</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.keypad}>
          {KEYS.map((k, i) => {
            const isOk = k === 'ok';
            const isClear = k === 'clear';
            const ready = pin.length === 4;
            return (
              <TouchableOpacity
                key={k}
                style={[
                  styles.key,
                  isOk && styles.keyOk,
                  isOk && !ready && styles.keyOkIdle,
                  !IS_PHONE && focused === i && styles.keyFocused,
                ]}
                onPress={() => handleKeyPress(k)}
                activeOpacity={0.65}
                accessibilityRole="button"
                accessibilityLabel={
                  isClear ? 'Delete' : isOk ? 'Confirm' : `Number ${k}`
                }
              >
                {isClear ? (
                  <Delete size={22} color={colors.ink2} strokeWidth={2.2} />
                ) : isOk ? (
                  <Check
                    size={24}
                    color={ready ? '#fff' : colors.ink3}
                    strokeWidth={3}
                  />
                ) : (
                  <Text style={styles.keyText}>{k}</Text>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {onForgot ? (
          <TouchableOpacity
            style={styles.forgotLink}
            onPress={onForgot}
            accessibilityRole="link"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text style={styles.forgotLinkText}>Forgot your PIN?</Text>
          </TouchableOpacity>
        ) : null}
      </Pressable>
    </Pressable>
  );
}

const IS_IOS = Platform.OS === 'ios';
const KEY_SIZE = IS_PHONE ? 66 : 74;
const KEY_GAP = IS_PHONE ? 12 : 14;
const DOT_GAP = 14;
const CARD_RADIUS = 34;

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(24,15,8,0.55)',
    alignItems: 'center',
    // Phone: sheet sits against the bottom. Elsewhere: centred dialog.
    justifyContent: IS_PHONE ? 'flex-end' : 'center',
    padding: IS_PHONE ? 0 : 20,
    zIndex: 100,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    borderRadius: CARD_RADIUS,
    overflow: 'hidden',
    alignItems: 'center',
    paddingTop: 26,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor:
      Platform.OS === 'ios' ? 'rgba(255,243,229,0.78)' : undefined,
    shadowColor: '#2A1A0A',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.28,
    shadowRadius: 30,
    elevation: 24,
  },
  cardSheet: {
    maxWidth: undefined,
    borderRadius: 0,
    borderTopLeftRadius: CARD_RADIUS,
    borderTopRightRadius: CARD_RADIUS,
    paddingTop: 14,
  },
  // The drag affordance every iOS sheet has.
  grabber: {
    width: 40,
    height: 5,
    borderRadius: 3,
    backgroundColor: 'rgba(46,35,22,0.18)',
    marginBottom: 12,
  },
  rim: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: CARD_RADIUS,
    borderWidth: StyleSheet.hairlineWidth * 1.5,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  // A flush sheet has no bottom edge to catch light — drawing one would just
  // put a stray hairline across the bottom of the screen.
  rimSheet: {
    borderRadius: 0,
    borderTopLeftRadius: CARD_RADIUS,
    borderTopRightRadius: CARD_RADIUS,
    borderBottomWidth: 0,
  },

  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(46,35,22,0.06)',
    zIndex: 2,
  },
  closeBtnText: {
    fontFamily,
    fontSize: 20,
    lineHeight: 22,
    fontWeight: '700',
    color: colors.ink2,
  },

  badge: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.bgWarm,
    borderWidth: StyleSheet.hairlineWidth * 2,
    borderColor: 'rgba(255,255,255,0.9)',
    marginBottom: 12,
  },
  title: {
    fontFamily,
    fontSize: 22,
    fontWeight: '900',
    color: colors.ink,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily,
    fontSize: 14,
    fontWeight: '600',
    color: colors.ink3,
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 18,
    paddingHorizontal: 8,
    lineHeight: 20,
  },

  // NO flex `gap` in this file. PinGate also runs on the TVs (TvApp →
  // ProfileSelect), and Tizen/WebOS sit on a Chromium 76 floor where flex gap
  // (Chromium 84+) is ignored — the dots and the keypad would collapse into
  // each other with no spacing. Symmetric margins instead: half the gap on each
  // side of every child yields the full gap between neighbours, and the block
  // is centred so the outer half-margin is invisible.
  dots: { flexDirection: 'row' },
  dot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginHorizontal: DOT_GAP / 2,
    borderWidth: 2,
    borderColor: 'rgba(46,35,22,0.22)',
    backgroundColor: 'transparent',
  },
  dotFilled: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  dotError: { borderColor: '#D9534F' },

  errorText: {
    fontFamily,
    fontSize: 13.5,
    fontWeight: '800',
    color: '#C0453F',
    marginTop: 10,
    marginBottom: 4,
    height: 18,
  },
  // Reserve the error line's height so the keypad never jumps.
  errorSpacer: { height: 32 },

  noPinBlock: { marginBottom: 8, alignItems: 'center' },
  noPinText: {
    fontFamily,
    fontSize: 13,
    fontWeight: '600',
    color: colors.ink2,
    textAlign: 'center',
    lineHeight: 19,
    paddingHorizontal: 6,
  },
  exitAnywayBtn: {
    marginTop: 10,
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 999,
    backgroundColor: colors.bgWarm,
  },
  exitAnywayText: {
    fontFamily,
    fontSize: 14,
    fontWeight: '800',
    color: colors.primaryInk,
  },

  keypad: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    // Width must include each key's own margins or the third key wraps.
    width: (KEY_SIZE + KEY_GAP) * 3,
  },
  key: {
    width: KEY_SIZE,
    height: KEY_SIZE,
    margin: KEY_GAP / 2,
    borderRadius: KEY_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    // A translucent white key with a white rim only reads on iOS, where the
    // BlurView behind it supplies the contrast. Everywhere else the card falls
    // back to flat #FFFBF5, so white-on-white left the keys all but invisible —
    // only the digits and the orange confirm were discernible. Give the
    // non-blurred platforms an opaque face and a warm hairline edge instead.
    backgroundColor: IS_IOS ? 'rgba(255,255,255,0.72)' : '#FFFFFF',
    borderWidth: IS_IOS ? StyleSheet.hairlineWidth * 1.5 : 1,
    borderColor: IS_IOS ? 'rgba(255,255,255,0.95)' : colors.lineStrong,
    shadowColor: '#2A1A0A',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
  },
  keyText: {
    fontFamily,
    fontSize: 26,
    fontWeight: '700',
    color: colors.ink,
    fontVariant: ['tabular-nums'],
  },
  // Confirm is the one filled key, so the primary action is unmistakable.
  keyOk: { backgroundColor: colors.primary, borderColor: colors.primary },
  keyOkIdle: {
    backgroundColor: IS_IOS ? 'rgba(255,255,255,0.72)' : '#FFFFFF',
    borderColor: IS_IOS ? 'rgba(255,255,255,0.95)' : colors.lineStrong,
  },
  // TV/remote focus — a bright halo (this component also runs on the TV).
  keyFocused: {
    borderColor: colors.primary,
    borderWidth: 3,
    transform: [{ scale: 1.06 }],
  },

  forgotLink: { marginTop: 16, paddingVertical: 6 },
  forgotLinkText: {
    fontFamily,
    fontSize: 14,
    fontWeight: '800',
    color: colors.primaryInk,
  },
});
